import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, Subscription, debounceTime } from 'rxjs';
import {
  alertModalDelete,
  alertModalWithoutConfirm,
  alertToast,
} from 'src/app/component/helpers/alert';
import {
  KB_MAX_FILES_PER_UPLOAD,
  KbFolder,
  KbItem,
  KbItemType,
  KbMe,
  KbPresigned,
  KbVisibility,
  KnowledgeBaseService,
  kbSize,
} from 'src/app/service/knowledge-base.service';

type View = 'home' | 'folder' | 'search';
/** company | department | private | all-departments (HR / Admin only). */
type Space = 'company' | 'department' | 'private' | 'all-departments';

interface UploadRow {
  file: File;
  percent: number;
  status: 'waiting' | 'uploading' | 'done' | 'failed' | 'refused';
  message: string;
}

const TYPE_FILTERS: { value: KbItemType | ''; label: string; icon: string }[] = [
  { value: '', label: 'Everything', icon: 'fa-layer-group' },
  { value: 'documents', label: 'Documents', icon: 'fa-file-alt' },
  { value: 'links', label: 'Links', icon: 'fa-link' },
  { value: 'images', label: 'Images', icon: 'fa-image' },
  { value: 'videos', label: 'Videos', icon: 'fa-video' },
  { value: 'audios', label: 'Audio', icon: 'fa-headphones' },
  { value: 'others', label: 'Other files', icon: 'fa-file' },
];

/**
 * Vault's second section (founder brief, 22–23 Oct 2026): the company's SOPs, policies, templates
 * and reference links in company / department / private folders, two levels deep, with one search
 * over everything the person may see. The server (`/knowledge-base`) decides every right; this
 * screen only asks `me` and trusts the `canWrite` / `canManage` / `canEdit` / `canDelete` flags
 * that come back on each folder and item.
 */
@Component({
  selector: 'app-knowledge-base',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './knowledge-base.component.html',
  styleUrl: './knowledge-base.component.css',
})
export class KnowledgeBaseComponent implements OnInit, OnDestroy {
  me: KbMe | null = null;
  view: View = 'home';
  space: Space = 'company';
  loading = false;
  /** Media stays gated by the VAULT module; the knowledge base is for everyone signed in. */
  hasMediaSection = false;

  // home
  topFolders: KbFolder[] = [];
  recent: KbItem[] = [];
  /** HR / Admin: department folders grouped by department. */
  departmentGroups: { department: string; folders: KbFolder[] }[] = [];

  // folder
  folder: KbFolder | null = null;
  breadcrumb: { _id: string; name: string }[] = [];
  subFolders: KbFolder[] = [];
  items: KbItem[] = [];
  total = 0;
  page = 1;
  limit = 30;
  typeFilter: KbItemType | '' = '';
  tagFilter = '';
  sort: 'newest' | 'oldest' | 'title' = 'newest';
  folderQuery = '';
  readonly typeFilters = TYPE_FILTERS;

  // search
  searchQuery = '';
  searchResults: { items: KbItem[]; folders: (KbFolder & { path?: string })[] } | null = null;
  searching = false;
  private search$ = new Subject<string>();
  private folderSearch$ = new Subject<string>();
  private subs: Subscription[] = [];

  // folder modal
  folderModal: {
    open: boolean;
    mode: 'create' | 'edit';
    parent: KbFolder | null;
    target: KbFolder | null;
    name: string;
    description: string;
    visibility: KbVisibility;
    department: string;
    saving: boolean;
  } = this.blankFolderModal();

  // upload modal
  uploadModal = { open: false, rows: [] as UploadRow[], running: false, dragging: false };
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('versionInput') versionInput?: ElementRef<HTMLInputElement>;

  // link modal
  linkModal = { open: false, url: '', title: '', description: '', tags: '', saving: false };

  // item drawer
  drawer: {
    open: boolean;
    item: KbItem | null;
    breadcrumb: { _id: string; name: string }[];
    editing: boolean;
    draft: { title: string; description: string; tags: string; url: string };
    saving: boolean;
    versionNote: string;
    versionBusy: boolean;
    versionPercent: number;
  } = this.blankDrawer();

  // move modal
  moveModal = {
    open: false,
    item: null as KbItem | null,
    folders: [] as KbFolder[],
    children: {} as Record<string, KbFolder[]>,
    expanded: {} as Record<string, boolean>,
    targetId: '',
    saving: false,
  };

  constructor(
    private kb: KnowledgeBaseService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const modules = (localStorage.getItem('modules') || '').toUpperCase();
    this.hasMediaSection = modules.split(',').map((m) => m.trim()).includes('VAULT');

    this.subs.push(
      this.search$.pipe(debounceTime(350)).subscribe((q) => this.runSearch(q)),
      this.folderSearch$.pipe(debounceTime(300)).subscribe(() => this.loadItems(1)),
      this.route.paramMap.subscribe((params) => {
        const folderId = params.get('folderId');
        if (!this.me) {
          this.kb.me().subscribe({
            next: (res: any) => {
              this.me = res?.data ?? null;
              folderId ? this.openFolderById(folderId) : this.loadSpace(this.space);
              const itemId = this.route.snapshot.queryParamMap.get('item');
              if (itemId) this.openItemById(itemId);
            },
            error: (err) => this.fail(err, 'Could not open the knowledge base'),
          });
        } else {
          folderId ? this.openFolderById(folderId) : this.loadSpace(this.space);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  // ------------------------------------------------------------------ navigation

  goHome(): void {
    this.router.navigate(['/home']);
  }

  goMedia(): void {
    this.router.navigate(['/vault']);
  }

  goKnowledgeHome(space?: Space): void {
    if (space) this.space = space;
    this.searchQuery = '';
    this.searchResults = null;
    if (this.router.url.startsWith('/vault/knowledge/folder')) {
      this.router.navigate(['/vault/knowledge']);
    } else {
      this.loadSpace(this.space);
    }
  }

  openFolder(folder: KbFolder | { _id: string }): void {
    this.searchQuery = '';
    this.searchResults = null;
    this.router.navigate(['/vault/knowledge/folder', folder._id]);
  }

  // ------------------------------------------------------------------ home

  get spaces(): { key: Space; label: string; icon: string; hint: string }[] {
    const list: { key: Space; label: string; icon: string; hint: string }[] = [
      { key: 'company', label: 'Company', icon: 'fa-building', hint: 'Everyone in the company' },
    ];
    // Admin and HR see every department, so one entry lists them all with their own first;
    // everyone else sees their own department only (a separate "yours" entry would be a subset).
    if (this.me?.canSeeAllDepartments) {
      list.push({
        key: 'all-departments',
        label: 'Departments',
        icon: 'fa-sitemap',
        hint: 'Every department, yours first',
      });
    } else if (this.me?.department) {
      list.push({
        key: 'department',
        label: this.me.department,
        icon: 'fa-users',
        hint: 'Your department',
      });
    }
    list.push({ key: 'private', label: 'Private', icon: 'fa-lock', hint: 'Only you' });
    return list;
  }

  loadSpace(space: Space): void {
    this.space = space;
    this.view = 'home';
    this.folder = null;
    this.loading = true;
    const filter =
      space === 'company'
        ? { visibility: 'company' }
        : space === 'department'
          ? { visibility: 'department', department: this.me?.department || '' }
          : space === 'all-departments'
            ? { visibility: 'department' }
            : { visibility: 'private' };
    this.kb.listFolders(filter).subscribe({
      next: (res: any) => {
        this.topFolders = res?.data ?? [];
        this.departmentGroups = [];
        if (space === 'all-departments') {
          const byDept = new Map<string, KbFolder[]>();
          for (const f of this.topFolders) {
            const key = f.department || '—';
            byDept.set(key, [...(byDept.get(key) ?? []), f]);
          }
          const mine = this.me?.department || '';
          this.departmentGroups = [...byDept.entries()]
            .sort((a, b) => (a[0] === mine ? -1 : b[0] === mine ? 1 : a[0].localeCompare(b[0])))
            .map(([department, folders]) => ({ department, folders }));
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.fail(err, 'Could not load folders');
      },
    });
    this.kb.recent(12).subscribe({
      next: (res: any) => (this.recent = res?.data?.items ?? []),
      error: () => (this.recent = []),
    });
  }

  // ------------------------------------------------------------------ folder view

  openFolderById(folderId: string): void {
    this.view = 'folder';
    this.loading = true;
    this.typeFilter = '';
    this.tagFilter = '';
    this.folderQuery = '';
    this.kb.getFolder(folderId).subscribe({
      next: (res: any) => {
        this.folder = res?.data?.folder ?? null;
        this.breadcrumb = res?.data?.breadcrumb ?? [];
        if (this.folder) {
          this.space =
            this.folder.visibility === 'company'
              ? 'company'
              : this.folder.visibility === 'private'
                ? 'private'
                : this.departmentSpace();
        }
        this.loadSubFolders();
        this.loadItems(1);
      },
      error: (err) => {
        this.loading = false;
        this.fail(err, 'That folder is not available');
        this.router.navigate(['/vault/knowledge']);
      },
    });
  }

  loadSubFolders(): void {
    if (!this.folder) return;
    this.kb.listFolders({ parentFolderId: this.folder._id }).subscribe({
      next: (res: any) => (this.subFolders = res?.data ?? []),
      error: () => (this.subFolders = []),
    });
  }

  loadItems(page = this.page): void {
    if (!this.folder) return;
    this.loading = true;
    this.kb
      .listItems({
        folderId: this.folder._id,
        type: this.typeFilter || undefined,
        tag: this.tagFilter || undefined,
        q: this.folderQuery.trim() || undefined,
        sort: this.sort,
        page,
        limit: this.limit,
      })
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? {};
          this.items = data.items ?? [];
          this.total = data.total ?? 0;
          this.page = data.page ?? page;
          if (data.folder) this.folder = { ...this.folder!, ...data.folder };
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.fail(err, 'Could not load this folder');
        },
      });
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.limit));
  }

  setType(value: KbItemType | ''): void {
    this.typeFilter = value;
    this.loadItems(1);
  }

  setTag(tag: string): void {
    this.tagFilter = this.tagFilter === tag ? '' : tag;
    this.loadItems(1);
  }

  setSort(sort: 'newest' | 'oldest' | 'title'): void {
    this.sort = sort;
    this.loadItems(1);
  }

  onFolderQuery(value: string): void {
    this.folderQuery = value;
    this.folderSearch$.next(value);
  }

  // ------------------------------------------------------------------ search

  onSearchInput(value: string): void {
    this.searchQuery = value;
    if (!value.trim()) {
      this.searchResults = null;
      this.view = this.folder ? 'folder' : 'home';
      return;
    }
    this.search$.next(value);
  }

  runSearch(q: string): void {
    const query = q.trim();
    if (!query) return;
    this.searching = true;
    this.view = 'search';
    this.kb.search(query, this.typeFilter || undefined).subscribe({
      next: (res: any) => {
        this.searchResults = {
          items: res?.data?.items ?? [],
          folders: res?.data?.folders ?? [],
        };
        this.searching = false;
      },
      error: (err) => {
        this.searching = false;
        this.fail(err, 'Search failed');
      },
    });
  }

  clearSearch(): void {
    this.onSearchInput('');
  }

  // ------------------------------------------------------------------ folders: create / edit / delete

  private blankFolderModal() {
    return {
      open: false,
      mode: 'create' as 'create' | 'edit',
      parent: null as KbFolder | null,
      target: null as KbFolder | null,
      name: '',
      description: '',
      visibility: 'company' as KbVisibility,
      department: '',
      saving: false,
    };
  }

  openCreateFolder(parent: KbFolder | null = null): void {
    const m = this.blankFolderModal();
    m.open = true;
    m.parent = parent;
    m.visibility =
      parent?.visibility ??
      (this.space === 'private' ? 'private' : this.space === 'company' ? 'company' : 'department');
    m.department = parent?.department ?? (this.me?.department || '');
    this.folderModal = m;
  }

  openEditFolder(folder: KbFolder): void {
    const m = this.blankFolderModal();
    m.open = true;
    m.mode = 'edit';
    m.target = folder;
    m.name = folder.name;
    m.description = folder.description || '';
    m.visibility = folder.visibility;
    m.department = folder.department || this.me?.department || '';
    this.folderModal = m;
  }

  closeFolderModal(): void {
    this.folderModal = this.blankFolderModal();
  }

  saveFolder(): void {
    const m = this.folderModal;
    const name = m.name.trim();
    if (!name) return alertModalWithoutConfirm('error', 'Give the folder a name');
    m.saving = true;
    const body: any = { name, description: m.description.trim() };
    if (m.mode === 'create') {
      if (m.parent) body.parentFolderId = m.parent._id;
      else {
        body.visibility = m.visibility;
        if (m.visibility === 'department') body.department = m.department;
      }
      this.kb.createFolder(body).subscribe({
        next: (res: any) => {
          alertToast('success', 'Folder created');
          this.closeFolderModal();
          if (m.parent) {
            this.loadSubFolders();
            if (this.folder) this.folder.subFolderCount = (this.folder.subFolderCount || 0) + 1;
          } else {
            const created: KbFolder | undefined = res?.data;
            const space: Space =
              created?.visibility === 'company'
                ? 'company'
                : created?.visibility === 'private'
                  ? 'private'
                  : this.departmentSpace();
            this.goKnowledgeHome(space);
          }
        },
        error: (err) => {
          m.saving = false;
          this.fail(err, 'Could not create the folder');
        },
      });
    } else if (m.target) {
      if (!m.target.parentFolderId) {
        body.visibility = m.visibility;
        if (m.visibility === 'department') body.department = m.department;
      }
      this.kb.updateFolder(m.target._id, body).subscribe({
        next: (res: any) => {
          alertToast('success', 'Folder updated');
          const updated: KbFolder = res?.data;
          if (this.folder && this.folder._id === updated?._id) {
            this.folder = { ...this.folder, ...updated };
            this.breadcrumb = this.breadcrumb.map((b) =>
              b._id === updated._id ? { ...b, name: updated.name } : b
            );
          }
          this.topFolders = this.topFolders.map((f) => (f._id === updated?._id ? { ...f, ...updated } : f));
          this.subFolders = this.subFolders.map((f) => (f._id === updated?._id ? { ...f, ...updated } : f));
          this.closeFolderModal();
        },
        error: (err) => {
          m.saving = false;
          this.fail(err, 'Could not update the folder');
        },
      });
    }
  }

  async deleteFolder(folder: KbFolder): Promise<void> {
    const what = folder.itemCount
      ? `"${folder.name}" and its ${folder.itemCount} item${folder.itemCount === 1 ? '' : 's'}`
      : `"${folder.name}"`;
    const ok = await alertModalDelete(`Delete ${what}?`, 'Sub-folders go with it. Files stay in storage until a clean-up.');
    if (!ok) return;
    this.kb.deleteFolder(folder._id).subscribe({
      next: () => {
        alertToast('success', 'Folder deleted');
        if (this.folder && this.folder._id === folder._id) {
          const parent = this.breadcrumb.length > 1 ? this.breadcrumb[this.breadcrumb.length - 2] : null;
          parent ? this.openFolder(parent) : this.goKnowledgeHome();
        } else {
          this.subFolders = this.subFolders.filter((f) => f._id !== folder._id);
          this.topFolders = this.topFolders.filter((f) => f._id !== folder._id);
          this.departmentGroups = this.departmentGroups
            .map((g) => ({ ...g, folders: g.folders.filter((f) => f._id !== folder._id) }))
            .filter((g) => g.folders.length);
          if (this.folder) this.folder.subFolderCount = Math.max(0, (this.folder.subFolderCount || 1) - 1);
        }
      },
      error: (err) => this.fail(err, 'Could not delete the folder'),
    });
  }

  // ------------------------------------------------------------------ uploads

  openUpload(): void {
    this.uploadModal = { open: true, rows: [], running: false, dragging: false };
  }

  closeUpload(): void {
    if (this.uploadModal.running) return;
    this.uploadModal.open = false;
    if (this.uploadModal.rows.some((r) => r.status === 'done')) this.loadItems(1);
    this.uploadModal.rows = [];
  }

  pickFiles(): void {
    this.fileInput?.nativeElement.click();
  }

  onFilesPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.uploadModal.dragging = true;
  }

  onDragLeave(): void {
    this.uploadModal.dragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.uploadModal.dragging = false;
    this.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  addFiles(files: File[]): void {
    for (const file of files) {
      if (this.uploadModal.rows.length >= KB_MAX_FILES_PER_UPLOAD) {
        alertModalWithoutConfirm('error', `Up to ${KB_MAX_FILES_PER_UPLOAD} files at a time`);
        break;
      }
      if (this.uploadModal.rows.some((r) => r.file.name === file.name && r.file.size === file.size)) continue;
      const reason = this.kb.rejectReason(file);
      this.uploadModal.rows.push({
        file,
        percent: 0,
        status: reason ? 'refused' : 'waiting',
        message: reason ?? '',
      });
    }
  }

  removeRow(row: UploadRow): void {
    if (this.uploadModal.running) return;
    this.uploadModal.rows = this.uploadModal.rows.filter((r) => r !== row);
  }

  get uploadDone(): boolean {
    return this.uploadModal.rows.some((r) => r.status === 'done');
  }

  get uploadReady(): UploadRow[] {
    return this.uploadModal.rows.filter((r) => r.status === 'waiting');
  }

  async startUpload(): Promise<void> {
    if (!this.folder || !this.uploadReady.length || this.uploadModal.running) return;
    this.uploadModal.running = true;
    const rows = this.uploadReady;
    let presigned: KbPresigned[] = [];
    try {
      const res: any = await this.kb.presign(this.folder._id, rows.map((r) => r.file)).toPromise();
      presigned = res?.data ?? [];
    } catch (err) {
      this.uploadModal.running = false;
      for (const r of rows) {
        r.status = 'failed';
        r.message = this.errorText(err, 'Could not start the upload');
      }
      return;
    }
    const completed: { itemId: string; uploadId: string; parts: { PartNumber: number; ETag: string }[] }[] = [];
    for (const row of rows) {
      const plan = presigned.find((p) => p.fileName === row.file.name);
      if (!plan) {
        row.status = 'failed';
        row.message = 'The server did not return an upload slot for this file';
        continue;
      }
      row.status = 'uploading';
      try {
        const parts = await this.kb.uploadParts(
          { file: row.file, urls: plan.urls, uploadId: plan.uploadId },
          (p) => (row.percent = p)
        );
        completed.push({ itemId: plan.itemId, uploadId: plan.uploadId, parts });
        row.percent = 100;
        row.status = 'done';
      } catch (err: any) {
        row.status = 'failed';
        row.message = err?.message || 'Upload failed';
      }
    }
    if (completed.length) {
      try {
        const res: any = await this.kb.completeUpload(completed).toPromise();
        const failed: { itemId: string; message: string }[] = res?.data?.failed ?? [];
        for (const f of failed) {
          const plan = presigned.find((p) => p.itemId === f.itemId);
          const row = rows.find((r) => r.file.name === plan?.fileName);
          if (row) {
            row.status = 'failed';
            row.message = f.message;
          }
        }
        const okCount = rows.filter((r) => r.status === 'done').length;
        if (okCount) alertToast('success', `${okCount} file${okCount === 1 ? '' : 's'} added`);
      } catch (err) {
        for (const row of rows.filter((r) => r.status === 'done')) {
          row.status = 'failed';
          row.message = this.errorText(err, 'Could not finish the upload');
        }
      }
    }
    this.uploadModal.running = false;
    this.loadItems(1);
  }

  // ------------------------------------------------------------------ links

  openLink(): void {
    this.linkModal = { open: true, url: '', title: '', description: '', tags: '', saving: false };
  }

  closeLink(): void {
    this.linkModal.open = false;
  }

  saveLink(): void {
    if (!this.folder) return;
    const url = this.linkModal.url.trim();
    if (!/^https?:\/\//i.test(url)) {
      return alertModalWithoutConfirm('error', 'Paste a full web address starting with http:// or https://');
    }
    this.linkModal.saving = true;
    this.kb
      .addLink({
        folderId: this.folder._id,
        url,
        title: this.linkModal.title.trim() || undefined,
        description: this.linkModal.description.trim() || undefined,
        tags: this.splitTags(this.linkModal.tags),
      })
      .subscribe({
        next: () => {
          alertToast('success', 'Link saved');
          this.closeLink();
          this.loadItems(1);
        },
        error: (err) => {
          this.linkModal.saving = false;
          this.fail(err, 'Could not save the link');
        },
      });
  }

  // ------------------------------------------------------------------ item drawer

  private blankDrawer() {
    return {
      open: false,
      item: null as KbItem | null,
      breadcrumb: [] as { _id: string; name: string }[],
      editing: false,
      draft: { title: '', description: '', tags: '', url: '' },
      saving: false,
      versionNote: '',
      versionBusy: false,
      versionPercent: 0,
    };
  }

  openItem(item: KbItem): void {
    this.openItemById(item._id);
  }

  openItemById(itemId: string): void {
    this.kb.getItem(itemId).subscribe({
      next: (res: any) => {
        const d = this.blankDrawer();
        d.open = true;
        d.item = res?.data?.item ?? null;
        d.breadcrumb = res?.data?.breadcrumb ?? [];
        this.drawer = d;
      },
      error: (err) => this.fail(err, 'That item is not available'),
    });
  }

  closeDrawer(): void {
    this.drawer = this.blankDrawer();
  }

  startEdit(): void {
    const item = this.drawer.item;
    if (!item) return;
    this.drawer.editing = true;
    this.drawer.draft = {
      title: item.title,
      description: item.description || '',
      tags: (item.tags || []).join(', '),
      url: item.link?.url || '',
    };
  }

  cancelEdit(): void {
    this.drawer.editing = false;
  }

  saveEdit(): void {
    const item = this.drawer.item;
    if (!item) return;
    const d = this.drawer.draft;
    const body: any = {
      title: d.title.trim(),
      description: d.description.trim(),
      tags: this.splitTags(d.tags),
    };
    if (item.kind === 'link' && d.url.trim() && d.url.trim() !== item.link?.url) body.url = d.url.trim();
    if (!body.title) return alertModalWithoutConfirm('error', 'Give it a title');
    this.drawer.saving = true;
    this.kb.updateItem(item._id, body).subscribe({
      next: (res: any) => {
        const updated: KbItem = res?.data;
        this.drawer.item = { ...item, ...updated };
        this.drawer.editing = false;
        this.drawer.saving = false;
        this.replaceItem(this.drawer.item);
        alertToast('success', 'Saved');
      },
      error: (err) => {
        this.drawer.saving = false;
        this.fail(err, 'Could not save');
      },
    });
  }

  async deleteItem(item: KbItem): Promise<void> {
    const ok = await alertModalDelete(
      `Delete "${item.title}"?`,
      item.kind === 'file' ? 'Every version of the file is removed from storage.' : 'The saved link is removed.'
    );
    if (!ok) return;
    this.kb.deleteItem(item._id).subscribe({
      next: () => {
        alertToast('success', 'Deleted');
        this.items = this.items.filter((i) => i._id !== item._id);
        this.total = Math.max(0, this.total - 1);
        if (this.folder && this.folder._id === item.folderId) this.folder.itemCount = Math.max(0, (this.folder.itemCount || 1) - 1);
        if (this.searchResults) this.searchResults.items = this.searchResults.items.filter((i) => i._id !== item._id);
        this.recent = this.recent.filter((i) => i._id !== item._id);
        if (this.drawer.item?._id === item._id) this.closeDrawer();
      },
      error: (err) => this.fail(err, 'Could not delete'),
    });
  }

  open(item: KbItem): void {
    const url = item.kind === 'link' ? item.link?.url : item.fileUrl;
    if (url) window.open(url, '_blank', 'noopener');
  }

  pickVersion(): void {
    this.versionInput?.nativeElement.click();
  }

  async onVersionPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const item = this.drawer.item;
    if (!file || !item) return;
    const reason = this.kb.rejectReason(file);
    if (reason) return alertModalWithoutConfirm('error', reason);
    this.drawer.versionBusy = true;
    this.drawer.versionPercent = 0;
    try {
      const pre: any = await this.kb.versionPresign(item._id, file, this.drawer.versionNote.trim()).toPromise();
      const plan = pre?.data;
      const parts = await this.kb.uploadParts(
        { file, urls: plan.urls, uploadId: plan.uploadId },
        (p) => (this.drawer.versionPercent = p)
      );
      const done: any = await this.kb
        .versionComplete(item._id, { versionId: plan.versionId, uploadId: plan.uploadId, parts })
        .toPromise();
      this.drawer.item = { ...item, ...(done?.data ?? {}) };
      this.drawer.versionNote = '';
      this.replaceItem(this.drawer.item!);
      alertToast('success', 'New version saved');
    } catch (err) {
      this.fail(err, 'Could not add the version');
    } finally {
      this.drawer.versionBusy = false;
    }
  }

  // ------------------------------------------------------------------ move

  openMove(item: KbItem): void {
    this.moveModal = { open: true, item, folders: [], children: {}, expanded: {}, targetId: '', saving: false };
    this.kb.listFolders({}).subscribe({
      next: (res: any) => {
        this.moveModal.folders = (res?.data ?? []).filter((f: KbFolder) => f.canWrite);
      },
      error: (err) => this.fail(err, 'Could not list folders'),
    });
  }

  toggleMoveChildren(folder: KbFolder): void {
    const m = this.moveModal;
    m.expanded[folder._id] = !m.expanded[folder._id];
    if (m.expanded[folder._id] && !m.children[folder._id]) {
      this.kb.listFolders({ parentFolderId: folder._id }).subscribe({
        next: (res: any) => (m.children[folder._id] = (res?.data ?? []).filter((f: KbFolder) => f.canWrite)),
        error: () => (m.children[folder._id] = []),
      });
    }
  }

  closeMove(): void {
    this.moveModal.open = false;
  }

  confirmMove(): void {
    const m = this.moveModal;
    if (!m.item || !m.targetId || m.targetId === m.item.folderId) return;
    m.saving = true;
    this.kb.moveItem(m.item._id, m.targetId).subscribe({
      next: () => {
        alertToast('success', 'Moved');
        const movedId = m.item!._id;
        this.items = this.items.filter((i) => i._id !== movedId);
        this.total = Math.max(0, this.total - 1);
        if (this.folder && this.folder._id === m.item!.folderId) this.folder.itemCount = Math.max(0, (this.folder.itemCount || 1) - 1);
        if (this.drawer.item?._id === movedId) this.closeDrawer();
        this.closeMove();
      },
      error: (err) => {
        m.saving = false;
        this.fail(err, 'Could not move');
      },
    });
  }

  // ------------------------------------------------------------------ helpers

  private replaceItem(updated: KbItem): void {
    this.items = this.items.map((i) => (i._id === updated._id ? { ...i, ...updated } : i));
    if (this.searchResults) {
      this.searchResults.items = this.searchResults.items.map((i) => (i._id === updated._id ? { ...i, ...updated } : i));
    }
    this.recent = this.recent.map((i) => (i._id === updated._id ? { ...i, ...updated } : i));
  }

  splitTags(raw: string): string[] {
    return Array.from(
      new Set(
        raw
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
      )
    ).slice(0, 15);
  }

  iconFor(item: KbItem): string {
    if (item.kind === 'link') {
      const host = item.link?.host || '';
      if (/google/.test(host)) return 'fab fa-google';
      if (/notion/.test(host)) return 'fas fa-book-open';
      if (/youtube|youtu\.be|loom|vimeo/.test(host)) return 'fas fa-play-circle';
      if (/figma/.test(host)) return 'fab fa-figma';
      return 'fas fa-link';
    }
    const ext = (item.extension || '').toLowerCase();
    if (ext === '.pdf') return 'fas fa-file-pdf';
    if (['.doc', '.docx', '.odt', '.rtf'].includes(ext)) return 'fas fa-file-word';
    if (['.xls', '.xlsx', '.csv', '.ods'].includes(ext)) return 'fas fa-file-excel';
    if (['.ppt', '.pptx', '.key', '.odp'].includes(ext)) return 'fas fa-file-powerpoint';
    if (['.zip', '.rar', '.7z', '.tar', '.gz'].includes(ext)) return 'fas fa-file-archive';
    switch (item.mediaType) {
      case 'images':
        return 'fas fa-file-image';
      case 'videos':
        return 'fas fa-file-video';
      case 'audios':
        return 'fas fa-file-audio';
      case 'documents':
        return 'fas fa-file-alt';
      default:
        return 'fas fa-file';
    }
  }

  typeClass(item: KbItem): string {
    return item.kind === 'link' ? 'links' : String(item.mediaType || 'others');
  }

  typeLabel(item: KbItem): string {
    if (item.kind === 'link') return item.link?.host?.replace(/^www\./, '') || 'link';
    return (item.extension || '').replace('.', '').toUpperCase() || 'file';
  }

  visibilityLabel(folder: KbFolder): string {
    if (folder.visibility === 'company') return 'Company';
    if (folder.visibility === 'private') return 'Private';
    return folder.department || 'Department';
  }

  size(bytes: number | undefined): string {
    return kbSize(bytes);
  }

  when(value: string | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days} days ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  /** Where department folders live in the rail for this person. */
  private departmentSpace(): Space {
    return this.me?.canSeeAllDepartments ? 'all-departments' : 'department';
  }

  /** The heading of the current space. */
  get spaceTitle(): string {
    switch (this.space) {
      case 'company':
        return 'Company knowledge';
      case 'department':
        return `${this.me?.department || 'Department'} knowledge`;
      case 'all-departments':
        return 'Departments';
      default:
        return 'Private';
    }
  }

  /** Inside a folder a tag narrows the folder; anywhere else it becomes a search. */
  onTagClick(tag: string): void {
    if (this.view === 'folder') this.setTag(tag);
    else this.onSearchInput(tag);
  }

  trackById(_: number, row: { _id: string }): string {
    return row._id;
  }

  private errorText(err: any, fallback: string): string {
    const m = err?.error?.message ?? err?.message;
    const text = Array.isArray(m) ? m.map((x: any) => x?.message || x).join('; ') : typeof m === 'object' ? m?.message : m;
    return typeof text === 'string' && text.trim() ? text : fallback;
  }

  private fail(err: any, fallback: string): void {
    alertModalWithoutConfirm('error', this.errorText(err, fallback));
  }
}
